import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, RefreshCw, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { InfoHint } from "@dolfin/ui";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { CopyButton } from "@/components/ui/copy-button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateTime } from "@/lib/format";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";

type Sent = components["schemas"]["OutboundEInvoiceAdminView"];
type DeliveryState = NonNullable<components["schemas"]["OutboundEInvoiceState"]>;
type ProviderFault = components["schemas"]["ProviderFaultDetail"];

interface SentEInvoicesTableProps {
  state?: DeliveryState;
  networkReference?: string;
  organisationId?: string;
  /** Bumping this triggers a refetch. */
  refreshKey: number;
}

/** Nothing to show. A single hyphen, per the house rule on user-visible text. */
const EMPTY = "-";

function stateVariant(state: string): "default" | "secondary" | "outline" | "destructive" {
  switch (state) {
    case "Delivered":
      return "default";
    case "DeliveryFailed":
      return "destructive";
    case "Abandoned":
    case "MadeAvailable":
      return "secondary";
    default:
      return "outline";
  }
}

function formatMoney(amount: string | number | null | undefined, currency: string | null | undefined) {
  if (amount === null || amount === undefined) return EMPTY;
  const value = typeof amount === "string" ? Number(amount) : amount;
  if (!Number.isFinite(value)) return EMPTY;
  return `${value.toFixed(2)} ${currency ?? ""}`.trim();
}

function networkLabel(d: Sent) {
  const network = d.network === "Sdi" ? "SDI" : d.network === "Peppol" ? "Peppol" : (d.network ?? EMPTY);
  return `${network}${d.country ? ` · ${d.country}` : ""}`;
}

/** When the delivery last moved, for the list. */
function lastMoved(d: Sent) {
  return d.deliveredAt ?? d.madeAvailableAt ?? d.failedAt ?? d.acceptedAt ?? d.submittedAt ?? d.createdAt;
}

export function SentEInvoicesTable({ state, networkReference, organisationId, refreshKey }: SentEInvoicesTableProps) {
  const [sent, setSent] = useState<Sent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Sent | null>(null);
  const pagination = useCursorPagination({ initialLimit: 50 });

  // A cursor is a position within one filtered ordering; reset it during render when the filter
  // changes (see ReceivedEInvoicesTable for why not in an effect).
  const filterKey = `${state ?? ""}|${networkReference ?? ""}|${organisationId ?? ""}`;
  const [cursorFilterKey, setCursorFilterKey] = useState(filterKey);
  if (cursorFilterKey !== filterKey) {
    setCursorFilterKey(filterKey);
    pagination.reset();
  }

  useEffect(() => {
    let cancelled = false;

    async function fetchSent() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/outbound-e-invoices", {
        params: {
          query: {
            state,
            networkReference,
            organisationId,
            cursor: pagination.cursor,
            limit: pagination.limit,
          },
        },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load sent e-invoices.");
      } else {
        setSent(data?.data ?? []);
        pagination.setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchSent();
    return () => {
      cancelled = true;
    };
  }, [state, networkReference, organisationId, refreshKey, pagination.cursor, pagination.limit]);

  if (error) return <p className="text-destructive">{error}</p>;
  if (loading && sent.length === 0) return <Spinner />;

  const pager = (
    <PaginationBar
      hasPrevious={pagination.hasPrevious}
      hasNext={pagination.hasMore}
      isLoading={loading}
      limit={pagination.limit}
      limitOptions={[20, 50, 100]}
      onPrevious={pagination.goPrevious}
      onNext={pagination.goNext}
      onLimitChange={pagination.setLimit}
    />
  );

  if (sent.length === 0) {
    return (
      <div>
        <p className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
          {networkReference
            ? `No sent document has network id ${networkReference}. If it was received rather than sent, try Received E-Invoices.`
            : "Nothing sent in this state."}
        </p>
        {pager}
      </div>
    );
  }

  return (
    <div>
      <div className="flex h-5 items-center justify-end">{loading && <Spinner className="size-4" />}</div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>State</TableHead>
            <TableHead>Document</TableHead>
            <TableHead>Organisation</TableHead>
            <TableHead>Customer</TableHead>
            <TableHead>Network</TableHead>
            <TableHead>Network id</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead>Last update</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sent.map((d) => {
            // A search can match an earlier attempt's id; say so, or the row shows a different id
            // from the one that was searched for.
            const matchedEarlier = networkReference && d.networkReference !== networkReference;
            return (
              <TableRow key={d.id} className="cursor-pointer" onClick={() => setSelected(d)}>
                <TableCell>
                  <Badge variant={stateVariant(d.state ?? "")}>{d.state}</Badge>
                </TableCell>
                <TableCell>
                  <span className="font-mono text-xs">{d.documentNumber ?? EMPTY}</span>{" "}
                  {d.documentKind === "CreditNote" && (
                    <Badge variant="outline" className="ml-1 font-normal">
                      Credit note
                    </Badge>
                  )}
                  {d.sandbox && (
                    <Badge variant="outline" className="ml-1 border-amber-500/40 font-normal text-amber-700 dark:text-amber-400">
                      Test
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="max-w-[14rem] truncate">
                  <Link
                    to={`/organisations/${d.organisationId}`}
                    className="text-primary hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {d.organisationName ?? d.organisationId}
                  </Link>
                </TableCell>
                <TableCell className="max-w-[14rem] truncate">{d.customerName ?? EMPTY}</TableCell>
                <TableCell className="text-xs">{networkLabel(d)}</TableCell>
                <TableCell className="font-mono text-xs">
                  {d.networkReference ?? EMPTY}
                  {matchedEarlier && (
                    <span className="block font-sans text-amber-700 dark:text-amber-400">matched an earlier attempt</span>
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">{formatMoney(d.totalAmount, d.currency)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{formatDateTime(lastMoved(d))}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {pager}

      <SentEInvoiceSheet
        sent={selected}
        onClose={() => setSelected(null)}
        onUpdated={(updated) => {
          setSelected(updated);
          setSent((rows) => rows.map((row) => (row.id === updated.id ? updated : row)));
        }}
      />
    </div>
  );
}

function SentEInvoiceSheet({
  sent,
  onClose,
  onUpdated,
}: {
  sent: Sent | null;
  onClose: () => void;
  onUpdated: (updated: Sent) => void;
}) {
  const [busy, setBusy] = useState<"resync" | "resend" | null>(null);

  // Resync asks the provider now (the sweep, on demand); Resend sends the delivery again with the
  // organisation's own resend rules. Both answer with the delivery as it stands afterwards.
  async function act(action: "resync" | "resend") {
    if (!sent?.id || busy) return;
    setBusy(action);
    try {
      const { data, error } =
        action === "resync"
          ? await api.POST("/admin/outbound-e-invoices/{id}/resync", { params: { path: { id: sent.id } } })
          : await api.POST("/admin/outbound-e-invoices/{id}/resend", { params: { path: { id: sent.id } } });
      if (error || !data) {
        toast.error((error as { detail?: string } | undefined)?.detail ?? `Could not ${action} this delivery.`);
        return;
      }
      onUpdated(data);
      toast.success(action === "resync" ? `Resynced: ${data.state}.` : "Sent again.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <Sheet open={!!sent} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        className="w-full overflow-y-auto sm:max-w-xl"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          (e.currentTarget as HTMLElement | null)?.focus();
        }}
      >
        <SheetHeader>
          <SheetTitle>
            {sent?.documentNumber ?? "Sent e-invoice"}
            {sent?.documentKind === "CreditNote" ? " (credit note)" : ""}
          </SheetTitle>
          <SheetDescription>
            {sent?.organisationName ?? EMPTY} → {sent?.customerName ?? EMPTY} ·{" "}
            {formatMoney(sent?.totalAmount, sent?.currency)}
          </SheetDescription>
        </SheetHeader>

        {sent && (
          <div className="space-y-6 p-4 text-sm">
            <div className="flex flex-wrap items-center gap-4">
              <span className="inline-flex items-center gap-1.5">
                <Button variant="outline" size="sm" disabled={!!busy} onClick={() => void act("resync")}>
                  {busy === "resync" ? <Spinner className="size-4" /> : <RefreshCw className="size-4" />}
                  Resync
                </Button>
                <InfoHint label="Resync">
                  Asks Invopop for this delivery's latest result now, the same check the background sweep runs
                  every 15 minutes. For an SDI invoice still awaiting its verdict it also pulls in any SDI
                  reports we missed. It only records what the provider confirms, and it never sends anything.
                </InfoHint>
              </span>
              {sent.canResend ? (
                <span className="inline-flex items-center gap-1.5">
                  <Button variant="outline" size="sm" disabled={!!busy} onClick={() => void act("resend")}>
                    {busy === "resend" ? <Spinner className="size-4" /> : <Send className="size-4" />}
                    {sent.resendRebuildsDocument ? "Resend with current details" : "Resend"}
                  </Button>
                  <InfoHint label="Resend">
                    {sent.resendRebuildsDocument
                      ? "Nothing reached the network, so this rebuilds the document from the customer's and the organisation's details as they are now, then sends it. Fix those details first. If nothing has changed it runs again on the same document; if something has, it goes as the next revision under the same invoice number. It is refused when the network rejected a document that has not changed."
                      : "The recipient may already hold this document, so the exact same document is sent again (a new run on the same Invopop entry). Edits to the customer since then are not picked up: sending a different document under the same number could leave the buyer with two versions."}{" "}
                    This is exactly what the organisation's own Resend does, run on their behalf.
                  </InfoHint>
                </span>
              ) : null}
            </div>
            <Section title="Delivery">
              <Field label="State">
                <Badge variant={stateVariant(sent.state ?? "")}>{sent.state}</Badge>
              </Field>
              <Field label="Network">
                {networkLabel(sent)}
                {sent.sandbox ? " (test)" : ""}
              </Field>
              <Field label="Attempt / revision">
                {sent.attempt ?? EMPTY} / {sent.revision ?? EMPTY}
              </Field>
              <Field label="Failure">{sent.failureKind && sent.failureKind !== "None" ? sent.failureKind : EMPTY}</Field>
              <Field label="Submitted">{sent.submittedAt ? formatDateTime(sent.submittedAt) : EMPTY}</Field>
              <Field label="Accepted">{sent.acceptedAt ? formatDateTime(sent.acceptedAt) : EMPTY}</Field>
              <Field label="Delivered">{sent.deliveredAt ? formatDateTime(sent.deliveredAt) : EMPTY}</Field>
              <Field label="Failed">{sent.failedAt ? formatDateTime(sent.failedAt) : EMPTY}</Field>
            </Section>

            <Section title="Document">
              <Field label="Kind">{sent.documentKind === "CreditNote" ? "Credit note" : "Invoice"}</Field>
              <Field label="Number" mono>
                {sent.documentNumber ?? EMPTY}
              </Field>
              <Field label={sent.documentKind === "CreditNote" ? "Credited invoice" : "Invoice"} mono>
                {sent.invoiceId ? (
                  <Link to={`/invoices/${sent.invoiceId}`} className="break-all text-primary hover:underline">
                    {sent.invoiceId}
                  </Link>
                ) : (
                  EMPTY
                )}
              </Field>
              <Field label="Credit note" mono>
                <Copyable value={sent.creditNoteId}>{sent.creditNoteId ?? EMPTY}</Copyable>
              </Field>
              <Field label="Recipient" mono>
                {sent.recipientAddress ? `${sent.recipientScheme ?? ""} ${sent.recipientAddress}`.trim() : EMPTY}
              </Field>
              <Field label="Amount">{formatMoney(sent.totalAmount, sent.currency)}</Field>
            </Section>

            <Section title="Network ids">
              <Field label="Current" mono>
                <Copyable value={sent.networkReference}>{sent.networkReference ?? "none yet"}</Copyable>
              </Field>
              <Field label="All attempts, oldest first" mono>
                {(sent.networkReferences ?? []).length > 0 ? (sent.networkReferences ?? []).join(", ") : EMPTY}
              </Field>
            </Section>

            <Section title="Faults">
              <Field label="What went wrong" full>
                <FaultList faults={sent.faults} />
              </Field>
            </Section>

            <Section title="Network events">
              <Field label="Current document, oldest first" full>
                {(sent.networkEvents ?? []).length === 0 ? (
                  <span className="text-muted-foreground">none recorded</span>
                ) : (
                  <ul className="space-y-1">
                    {(sent.networkEvents ?? []).map((e) => (
                      <li key={e.id} className="text-muted-foreground">
                        <span className="font-medium text-foreground">{e.outcome}</span>
                        {e.networkCode ? ` (${e.networkCode})` : ""} · {formatDateTime(e.at)}
                        {e.description ? ` - ${e.description}` : ""}
                      </li>
                    ))}
                  </ul>
                )}
              </Field>
              {(sent.previousAttempts ?? []).length > 0 && (
                <Field label="Earlier attempts" full>
                  <ul className="space-y-1">
                    {(sent.previousAttempts ?? []).map((a) => (
                      <li key={`${a.attempt}-${a.revision}`} className="text-muted-foreground">
                        Attempt {a.attempt} (revision {a.revision}): {a.state}
                        {a.networkReference ? ` · network id ${a.networkReference}` : ""}
                        {a.failureKind && a.failureKind !== "None" ? ` · ${a.failureKind}` : ""}
                      </li>
                    ))}
                  </ul>
                </Field>
              )}
            </Section>

            <Section title="Identifiers">
              <Field label="Delivery" mono>
                <Copyable value={sent.id}>{sent.id ?? EMPTY}</Copyable>
              </Field>
              <Field label="Organisation" mono>
                <span className="flex items-start gap-2">
                  <Link to={`/organisations/${sent.organisationId}`} className="break-all text-primary hover:underline">
                    {sent.organisationId}
                  </Link>
                  {sent.organisationId && <CopyButton value={sent.organisationId} className="mt-0.5 shrink-0" />}
                </span>
              </Field>
            </Section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-3 border-b pb-1 text-xs font-semibold uppercase tracking-wide text-foreground">{title}</h3>
      <dl className="grid [&>div]:min-w-0 grid-cols-2 gap-x-4 gap-y-4">{children}</dl>
    </div>
  );
}

function Field({ label, children, mono, full }: { label: string; children: React.ReactNode; mono?: boolean; full?: boolean }) {
  return (
    <div className={full ? "col-span-2" : undefined}>
      <dt className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className={mono ? "font-mono text-xs break-all" : undefined}>{children}</dd>
    </div>
  );
}

function Copyable({ value, children }: { value?: string | null; children: React.ReactNode }) {
  if (!value) return <span className="font-sans text-muted-foreground">{children}</span>;
  return (
    <span className="flex items-start gap-2">
      <span className="break-all">{children}</span>
      <CopyButton value={value} className="mt-0.5 shrink-0" />
    </span>
  );
}

function FaultList({ faults }: { faults?: ProviderFault[] | null }) {
  if (!faults || faults.length === 0) return <span className="text-muted-foreground">none recorded</span>;
  return (
    <ul className="space-y-1">
      {faults.map((f, i) => (
        <li key={i} className="flex items-start gap-1.5 text-muted-foreground">
          <AlertTriangle className="mt-0.5 size-3 shrink-0 text-amber-600 dark:text-amber-400" />
          <span>
            {f.step ? <span className="font-medium text-foreground">{f.step}: </span> : null}
            {f.message}
            {f.code ? <span className="font-mono text-xs opacity-70"> ({f.code})</span> : null}
          </span>
        </li>
      ))}
    </ul>
  );
}
